import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/lib/api";
import { NotFound } from "@/pages/SimplePages";
import NAVIGATION from "@/data/navigation.json";

export default function ServicePage() {
  const { slug } = useParams();
  const { data } = useContent();
  const s = data?.settings || {};
  const service = (data?.services || []).find((x) => x.slug === slug);

  if (!data) return null;
  if (!service) return <NotFound />;

  const parent = service.parent ? data.services.find((x) => x.slug === service.parent) : null;
  const sisters = service.parent ? data.services.filter((x) => x.parent === service.parent && x.slug !== service.slug).slice(0, 2) : [];
  const children = data.services.filter((x) => x.parent === service.slug);
  const crumbs = [{ name: "Accueil", path: "/" }];
  if (parent) crumbs.push({ name: parent.h1, path: `/${parent.slug}/` });
  crumbs.push({ name: service.h1, path: `/${service.slug}/` });

  return (
    <main data-testid={`service-page-${service.slug}`}>
      <Seo
        title={service.seoTitle}
        description={service.seoMeta}
        path={`/${service.slug}/`}
      />
      <div className="page-banner" style={{ background: "var(--roi)" }}>
        <div className="wrap" style={{ paddingTop: 148, paddingBottom: 64 }}>
          <p className="t-label" style={{ color: "rgba(255,255,255,0.7)", marginBottom: 18 }} data-testid="service-breadcrumb">
            {crumbs.map((c) => c.name).join(" / ")}
          </p>
          <h1 className="t-h1" style={{ margin: 0, color: "#fff", maxWidth: 1000 }}>{service.h1}</h1>
        </div>
      </div>

      <div className="wrap" style={{ paddingTop: 72, paddingBottom: 96 }}>
        <Reveal><p className="t-chapo" style={{ maxWidth: 760, marginTop: 0, fontSize: "clamp(19px,1.8vw,24px)", color: "var(--ink)" }}>{service.chapeau}</p></Reveal>

        <div style={{ maxWidth: 860 }}>
          {service.sections.map((sec, i) => (
            <Reveal key={i} style={{}}>
              <section style={{ borderTop: "1px solid var(--li)", marginTop: 56, paddingTop: 40 }}>
                <h2 className="t-h3" style={{ margin: "0 0 20px" }}>{sec.h2}</h2>
                {sec.body.map((p, j) => (
                  <p className="t-body" key={j} style={{ marginTop: j === 0 ? 0 : 16 }}>{p}</p>
                ))}
              </section>
            </Reveal>
          ))}
        </div>

        {children.length > 0 && (
          <div style={{ marginTop: 72 }}>
            <p className="t-label" style={{ marginBottom: 20 }}>Les services</p>
            <div>
              {children.map((c) => (
                <Link to={`/${c.slug}/`} key={c.slug} className="exp-row" data-testid={`service-child-${c.slug}`}>
                  <span className="nom">{c.h1}</span>
                  <span className="det">Découvrir</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {service.faq?.length > 0 && (
          <div style={{ marginTop: 72 }}>
            <p className="t-label" style={{ marginBottom: 8 }}>Questions fréquentes</p>
            {service.faq.map((it, i) => (
              <details className="faq-item" key={i} style={{ padding: "20px 0" }}>
                <summary style={{ cursor: "pointer", fontVariationSettings: '"wdth" 104, "wght" 700', fontSize: 17 }} data-testid={`service-faq-${i}`}>{it.q}</summary>
                <p className="t-body" style={{ maxWidth: 760, marginTop: 14 }}>{it.a}</p>
              </details>
            ))}
          </div>
        )}

        <p className="t-body" style={{ marginTop: 80, maxWidth: 760 }} data-testid={NAVIGATION.appel_estimer.testid}>
          {NAVIGATION.appel_estimer.avant}
          <a href={NAVIGATION.appel_estimer.to}>{NAVIGATION.appel_estimer.mot}</a>
          {NAVIGATION.appel_estimer.apres}
        </p>

        <div style={{ borderTop: "1px solid var(--li)", marginTop: 80, paddingTop: 48, display: "flex", flexWrap: "wrap", gap: 32, justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ maxWidth: 560 }}>
            <h2 className="t-h3" style={{ margin: "0 0 12px", fontSize: 26 }}>30 minutes suffisent pour cadrer.</h2>
            <p className="t-body" style={{ margin: 0 }}>Dites l'objectif et le budget. On répond franchement.</p>
          </div>
          <Link to="/brief/" className="btn btn-primary" data-testid="service-cta-rdv">Prendre rendez-vous</Link>
        </div>

        {(parent || sisters.length > 0) && (
          <nav style={{ marginTop: 64 }} aria-label="Pages liées">
            <p className="t-label" style={{ marginBottom: 20 }}>À lire aussi</p>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {parent && <Link to={`/${parent.slug}/`} className="link-arrow" data-testid="link-pillar">{parent.h1} <ArrowRight size={13} strokeWidth={1.5} /></Link>}
              {sisters.map((x) => (
                <Link key={x.slug} to={`/${x.slug}/`} className="link-arrow" data-testid={`link-sister-${x.slug}`}>{x.h1} <ArrowRight size={13} strokeWidth={1.5} /></Link>
              ))}
            </div>
          </nav>
        )}
      </div>
    </main>
  );
}
